/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ScaleLinear } from '../utils/PRIVATE_chartUtils';
import { AxisPosition, Dimension, Group, GroupAxisProps } from './axis.types';
import { AXIS_DEFAULTS } from './utils/axisDefaults';
import { getAxisMaxSize, getAxisRange, getSafeSkips } from './utils/axisUtils';
import { getTitleProps } from './utils/titleUtils';
import { getLabelDimensions, getMaxTextDimensions, updateLabelsDims } from './utils/labelUtils';
import { useTextDimensions } from '../hooks/PRIVATE_useTextDimensions';
type GetTextDimensions = NonNullable<ReturnType<typeof useTextDimensions>['getTextDimensions']>;

/**
 * Retuns the groups axis labels.
 * @param groups The groups array.
 * @param startIndex The viewport start index.
 * @param endIndex The viewport end index.
 * @returns
 */
function getGroupAxisLabels(groups: Group[], startIndex: number, endIndex: number) {
  const ticks = [];
  for (let i = startIndex; i <= endIndex; i++) {
    ticks.push(groups[i].name || groups[i].id);
  }
  return ticks;
}

/**
 * Retuns the info about group axis.
 * @param groups The groups array.
 * @param xAxis The xAxis props.
 * @param offset The start and end offset for the chart.
 * @returns
 */
export function getGroupAxisInfo(groups: Group[], xAxis: GroupAxisProps = {}, offset: number) {
  let { viewportMin, viewportMax, viewportEndGroup, viewportStartGroup } = xAxis;

  if (viewportMin != null) {
    viewportStartGroup = groups[Math.max(0, Math.ceil(viewportMin))].id;
  }

  if (viewportStartGroup != null) {
    viewportMin = groups.findIndex((value: Group) => value.id === viewportStartGroup);
  }

  if (viewportMin == null) {
    viewportMin = -1 * offset;
    viewportStartGroup = groups[0]?.id;
  }

  if (viewportMax != null) {
    viewportEndGroup = groups[Math.min(groups.length - 1, Math.floor(viewportMax))].id;
  }

  if (viewportEndGroup != null) {
    viewportMax = groups.findIndex((value: Group) => value.id === viewportEndGroup);
  }

  if (viewportMax == null) {
    viewportMax = groups.length - 1 + offset;
    viewportEndGroup = groups[Math.floor(viewportMax)]?.id;
  }

  return {
    min: -1 * offset,
    max: groups.length - offset,
    viewportMin,
    viewportMax,
    viewportStartGroup,
    viewportEndGroup,
    startIndex: Math.max(0, Math.ceil(viewportMin!)),
    endIndex: Math.min(Math.floor(viewportMax), viewportMax!)
  };
}

/**
 * The function that returns the x axis properties.
 * @param groupAxisIno The GroupAxisInfo.
 * @param groupAxis The groupAxis props.
 * @param groups The chart groups.
 * @param position The position of axis in the given space.
 * @param availSpace The available space.
 * @param isRtl Whether the reading direction is right to left.
 * @param getTextDimensions Function for text measurement.
 * @returns
 */
export function getGroupAxisProps(
  groups: Group[],
  position: AxisPosition,
  availSpace: Dimension,
  isRtl: boolean,
  viewportMin: number,
  viewportMax: number,
  startIndex: number,
  endIndex: number,
  isDomainFlipped: boolean,
  groupAxis?: GroupAxisProps,
  axisFontSize?: string,
  getTextDimensions?: GetTextDimensions
) {
  const isHoriz = position === 'top' || position === 'bottom';
  const axisMaxDim = getAxisMaxSize(availSpace, isHoriz, groupAxis?.size);
  const titleStyle = { fontSize: axisFontSize, ...groupAxis?.titleStyle };

  const {
    width: titleWidth,
    height: titleHeight,
    ...titleProps
  } = getTitleProps(
    groupAxis?.title,
    titleStyle,
    axisMaxDim,
    availSpace,
    position,
    getTextDimensions
  );
  const gap = AXIS_DEFAULTS.labelGap;
  const isTitleRendered = titleWidth > 0 && titleWidth > 0;

  const [start, end] = getAxisRange(availSpace, position, isRtl);
  const domain: [number, number] = isDomainFlipped
    ? [viewportMax, viewportMin]
    : [viewportMin, viewportMax];
  const scale = new ScaleLinear(domain, [start, end]);
  const groupWidth = Math.abs(end - start) / (viewportMax - viewportMin);

  const tickLabelStyle = {
    fontSize: axisFontSize,
    fill: groupAxis?.tickLabel?.style?.color,
    ...groupAxis?.tickLabel?.style,
    textAnchor: position === 'left' ? 'end' : position === 'bottom' ? 'middle' : 'start',
    dominantBaseline: 'middle'
  };

  const labelsInView = getGroupAxisLabels(groups, startIndex, endIndex);

  const numOfSafeSkips = getSafeSkips(groupWidth, tickLabelStyle, getTextDimensions);

  const getLabelDims = (label: string, index: number) => {
    const rangeValue = scale.transform(index + startIndex);
    return getLabelDimensions(
      rangeValue,
      label,
      tickLabelStyle,
      isHoriz,
      availSpace,
      getTextDimensions
    );
  };

  const { maxWidth, maxHeight, tickLabels } = getMaxTextDimensions(
    labelsInView,
    getLabelDims,
    isHoriz,
    numOfSafeSkips
  );

  const isLabelRendered = isHoriz
    ? maxHeight + gap < axisMaxDim - titleHeight
    : maxWidth + gap < axisMaxDim - titleHeight;

  const getAxisPos = (index: number) => {
    return scale.transform(startIndex + index);
  };

  if (isLabelRendered) {
    updateLabelsDims(maxWidth, maxHeight, tickLabels, getAxisPos, availSpace, position, isRtl);
  }

  let axisDim = isLabelRendered ? (isHoriz ? maxHeight + gap : maxWidth + gap) : 0;
  if (isTitleRendered) {
    axisDim += titleHeight;
  }

  return {
    title: isTitleRendered ? groupAxis?.title : undefined,
    titleProps: isTitleRendered ? titleProps : undefined,
    tickLabels,
    tickLabelStyle,
    axisDim,
    scale,
    groupWidth
  };
}
